import { reducer } from './reducer/reducer.js';
import {
  defaultState,
  type InitialState,
  type State,
} from './reducer/state.js';
import { useCallback, useMemo, useReducer } from 'react';

export const usePayoutSetupWorkflow = (initialState: InitialState) => {
  const [state, dispatch] = useReducer(reducer, {
    ...defaultState,
    ...initialState,
  });

  const start = useCallback(
    () =>
      dispatch({
        payload: {},
        type: 'START',
      }),
    [],
  );
  const cancel = useCallback(() => dispatch({ type: 'CANCEL' }), []);
  const goBack = useCallback(() => dispatch({ type: 'GO_BACK' }), []);
  const goForward = useCallback(() => dispatch({ type: 'GO_FORWARD' }), []);
  const setStripeBusinessType = useCallback(
    (businessType: State['selectedBusinessType']) =>
      dispatch({
        payload: {
          businessType,
        },
        type: 'SET_STRIPE_BUSINESS_TYPE',
      }),
    [],
  );
  const refreshAfterRedirectFromStripe = useCallback((isDebitCard: boolean) => {
    return dispatch({
      payload: {
        paymentMethod: isDebitCard
          ? 'PAYOUT_Stripe_Local_Debit_Card'
          : 'PAYOUT_Stripe_Local_Bank',
      },
      type: 'REFRESH_AFTER_STRIPE_ACCOUNT',
    });
  }, []);
  const connectPaypal = useCallback(
    (paypalAuthenticationCode: string) =>
      dispatch({
        payload: { paypalAuthenticationCode },
        type: 'CONNECT_PAYPAL',
      }),
    [],
  );
  const syncPayoneer = useCallback(
    () =>
      dispatch({
        type: 'SYNC_PAYONEER',
      }),
    [],
  );

  const setPayoutMethod = useCallback(
    (payoutType: State['selectedPayoutType']) =>
      dispatch({
        payload: payoutType,
        type: 'SET_PAYOUT_METHOD',
      }),
    [],
  );

  const setPayoutAccountEligibility = useCallback(
    (payoutAccountEligibility: State['payoutAccountEligibility']) =>
      dispatch({
        payload: payoutAccountEligibility,
        type: 'SET_PAYOUT_ACCOUNT_ELIGIBILITY',
      }),
    [],
  );

  const setInitialState = useCallback((payload: InitialState) => {
    dispatch({
      payload,
      type: 'SET_INITIAL_STATE',
    });
  }, []);

  const exposedState = useMemo<State>(
    () => ({
      canEditCountry: state.canEditCountry,
      initialCountryCode: state.initialCountryCode,
      payoutAccountEligibility: state.payoutAccountEligibility,
      payoutTypesEnabled: state.payoutTypesEnabled,
      paypalAuthenticationCode: state.paypalAuthenticationCode,
      selectedBusinessType: state.selectedBusinessType,
      selectedPayoutType: state.selectedPayoutType,
      stripeRequiresOnboardingAction: state.stripeRequiresOnboardingAction,
      verifiedIdentity: state.verifiedIdentity,
      view: state.view,
    }),
    [
      state.canEditCountry,
      state.initialCountryCode,
      state.payoutAccountEligibility,
      state.payoutTypesEnabled,
      state.paypalAuthenticationCode,
      state.selectedBusinessType,
      state.selectedPayoutType,
      state.stripeRequiresOnboardingAction,
      state.verifiedIdentity,
      state.view,
    ],
  );

  const actionFunctions = useMemo(
    () => ({
      cancel,
      connectPaypal,
      goBack,
      goForward,
      refreshAfterRedirectFromStripe,
      setInitialState,
      setPayoutAccountEligibility,
      setPayoutMethod,
      setStripeBusinessType,
      start,
      syncPayoneer,
    }),
    [
      cancel,
      connectPaypal,
      goBack,
      goForward,
      refreshAfterRedirectFromStripe,
      setInitialState,
      setPayoutAccountEligibility,
      setPayoutMethod,
      setStripeBusinessType,
      start,
      syncPayoneer,
    ],
  );

  return { actions: actionFunctions, state: exposedState };
};
